import React, { Suspense, useEffect, useRef } from "react";
import { ConfigProvider, Space, Button, notification, Modal } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { reset, set } from "automate-redux";
const AuthenticatedRoutes = lazyWithPreload(() => import("./AuthenticatedRoutes"));
import { getAccounts, loadMasters, loadCommonMasters } from "./thunks/master";
import { loadPrintSettings, loadSettings } from "./thunks/settings";
import {
  acknowledgeUpdate,
  createNewFinancialYear,
  getIsUpdateAcknowledged,
  getIsUpdateAvailable,
  getOrgDetails,
  getOrganizations,
  getProfile,
  getSelectedFcYearDetails,
  getSelectedFcYearId,
  getSelectedOrgId,
  getUserOrgDetails,
  loadOrganizationDetails,
  loadProfile,
  loadUserOrgGroups,
  loadUserOrganizations,
  updateApp,
} from "./thunks/user-management";
import { useIdleTimer } from "react-idle-timer";
import UnauthenticatedRoutes from "./UnauthenticatedRoutes";
import RequestAccess from "./pages/request-access/Index";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import { differenceInDays, format, subDays } from "date-fns";
import {
  SuspenseFallback,
  addAppTab,
  decrementPendingRequests,
  formatDate,
  incrementPendingRequests,
  lazyWithPreload,
  showReleaseNotesPopup,
  sleep,
  usePrevious
} from "./utils";
import store from "./store";
import packageInfo from "../package.json"
import { userOrgRoles } from "./constants";
import { fetchPendingEinvoices } from "./thunks/transactions";
import { fetchReport } from "./thunks/reports";

const App = () => {
  const dispatch = useDispatch();
  const profileLoaded = useSelector((state) => state.profileLoaded);
  const selectedOrgId = useSelector((state) => getSelectedOrgId(state));
  const previousSelectedOrgId = usePrevious(selectedOrgId);
  const selectedOrgDetails = useSelector((state) => getOrgDetails(state));
  const isUpdateAvailable = useSelector((state) => getIsUpdateAvailable(state));
  const selectedFcYearId = useSelector((state) => getSelectedFcYearId(state));
  const selectedFcYearDetails = useSelector(state => getSelectedFcYearDetails(state));
  const selectedSessionId = useSelector((state) => state.selectedSessionId);
  const screenLockPassed = useSelector((state) => state.screenLockPassed);
  const companySelectorPopupVisible = useSelector(state => state.companySelectorPopupVisible);
  const userOrgDetails = useSelector(state => getUserOrgDetails(state));
  const cashAccounts = useSelector(state => getAccounts(state)?.filter(obj => obj.type === "CASH"));
  const userOrgRole = userOrgDetails.role;
  const userOrgPermissions = userOrgDetails.permissions || {};
  const finalReportPermission = (userOrgRole === userOrgRoles.OWNER || userOrgPermissions.reports?.final?.read);
  const salesPermission = (userOrgRole === userOrgRoles.OWNER || userOrgPermissions.transactions?.SAL?.create);
  const profile = useSelector(state => getProfile(state));
  const onScreenLockTimeout = () => {
    window.activeElementBeforeScreenLock = document.activeElement;
    dispatch(set("screenLockPassed", false))
  };
  const orgs = useSelector((state) => getOrganizations(state));
  const org = orgs.find((obj) => obj.id === selectedOrgId);
  const requireAccessApproval =
    org?.requires_approval && new Date().toISOString() >= (org.expires_at || "");

  const screenLockTimeSeconds = profile.settings?.webScreenLockTime === 0 ? 24 * 60 * 60 : (profile.settings?.webScreenLockTime || 300);
  const screenLockTimer = useIdleTimer({
    onIdle: onScreenLockTimeout,
    timeout: screenLockTimeSeconds * 1000,
    startOnMount: false,
    startManually: true,
  });
  const popupAlertsQueueRef = useRef([]);
  const lastPopupAlertAt = useRef();
  const processPopupAlerts = () => {
    if (popupAlertsQueueRef.current.length) {
      const { fn, time } = popupAlertsQueueRef.current[0];
      const currentTime = new Date().getTime();
      if (time <= currentTime) {
        popupAlertsQueueRef.current.shift();
        fn();
      }
      if (popupAlertsQueueRef.current.length) {
        setTimeout(() => processPopupAlerts(), time - currentTime);
      }
    }
  }
  const addPopupAlertToQueue = (fn) => {
    const currentTime = new Date().getTime();
    const time = lastPopupAlertAt.current ? Math.max(lastPopupAlertAt.current, currentTime) + 300 : currentTime;
    lastPopupAlertAt.current = time;
    popupAlertsQueueRef.current.push({ fn, time });
    processPopupAlerts()
  }

  useEffect(() => {
    try {
      dispatch(loadCommonMasters());
    } catch (error) {
      console.log("error", error);
    }
  }, []);

  useEffect(() => {
    const handleEffect = async () => {
      if (selectedSessionId) {
        try {
          incrementPendingRequests();
          await Promise.all([dispatch(loadProfile()), dispatch(loadUserOrganizations(true)), dispatch(loadUserOrgGroups())]);
        } finally {
          decrementPendingRequests();
        }
      }
    }
    handleEffect();
  }, [selectedSessionId]);

  useEffect(() => {
    if (!screenLockPassed) {
      screenLockTimer.pause();
    } else {
      if (profile?.settings?.webScreenLockTime !== 0) {
        screenLockTimer.start();
        if (window.activeElementBeforeScreenLock) {
          window.activeElementBeforeScreenLock?.focus?.()
        }
      }
    }
  }, [screenLockPassed]);

  useEffect(() => {
    if (selectedOrgDetails?.id && screenLockPassed && !companySelectorPopupVisible) {
      const previousVersion = localStorage.getItem("version");
      const versionChanged = previousVersion && previousVersion !== packageInfo.version;
      if (localStorage.getItem("promptReleaseNotes") || versionChanged) {
        showReleaseNotesPopup();
      }
      localStorage.setItem("version", packageInfo.version);

      const freezeDate = selectedOrgDetails.freeze_date;
      const todayDate = format(new Date(), "yyyy-MM-dd");
      const isFreezed = todayDate >= freezeDate;
      const isTerminated = todayDate >= selectedOrgDetails.terminate_date;
      const showPaymentAlert = todayDate >= selectedOrgDetails.payment_alert_date;
      const modalRef = { current: null };
      if (isFreezed || isTerminated) {
        const title = <div>Your database is {isTerminated ? "terminated" : "freezed"} due to non payment of cloud subscription.
          Please clear your due payment to renew cloud subscription.
          क्लाउड सब्सक्रिप्शन का भुगतान न करने के कारण आपका डेटाबेस बंद हो गया है।
          क्लाउड सब्सक्रिप्शन पुनः आरंभ करने के लिए, कृपया बकाया पेमेंट जल्द से जल्द कीजिये ।
          <p style={{ fontWeight: "bold", color: "#d9363e" }}>For payment related query, please contact to: <br /> Rameshji 98984-15532</p>
        </div>
        modalRef.current = Modal.confirm({
          onOk: () => modalRef.current.destroy(),
          width: 600,
          icon: <ExclamationCircleOutlined style={{ color: "red" }} />,
          closable: false,
          okText: "Okay",
          okButtonProps: { size: "large", block: true },
          cancelButtonProps: { style: { visibility: "hidden" } },
          title: <span style={{ color: "red", fontSize: 32 }}>{title}</span>
        });
      } else if (showPaymentAlert) {
        const currentTime = new Date().getTime();
        const lastReminderTime = Number(localStorage.getItem("lastPaymentReminderTimeMs") || "0");
        const hoursSinceLastReminder = Math.floor((currentTime - lastReminderTime) / (1000 * 60 * 60));
        if (hoursSinceLastReminder >= 8) {
          localStorage.setItem("lastPaymentReminderTimeMs", currentTime);
          modalRef.current = Modal.confirm({
            onOk: () => modalRef.current.destroy(),
            closable: false,
            okText: "Okay",
            icon: null,
            cancelButtonProps: { style: { visibility: "hidden" } },
            title: <div style={{ fontWeight: "bold", color: "#d9363e" }}>Payment Reminder</div>,
            styles: {
              body: { background: "#fff2f0" },
              content: { background: "#fff2f0" },
            },
            content: <div>Please clear due payment of software {freezeDate ? ` before ${format(new Date(freezeDate), "dd MMM yyyy")}` : ""}
              to continue using software without any interruption.
              सॉफ़्टवेयर का उपयोग बिना किसी रुकावट के जारी रखने के लिए, कृपया सॉफ़्टवेयर का बकाया भुगतान
              {freezeDate ? `${format(new Date(freezeDate), "dd MMM yyyy")} के पहले` : ``} कर दीजिय ।
              <p style={{ fontWeight: "bold", color: "#d9363e" }}>For payment related query, please contact to: <br /> Rameshji 98984-15532</p></div>
          });
        }
      }

      if (selectedOrgDetails?.settings?.enable_einvoicing && (salesPermission || finalReportPermission)) {
        const currentTime = new Date().getTime();
        const lastReminderTime = Number(localStorage.getItem(`lastEinvoiceReminderTimeMs.${selectedOrgDetails?.id}`) || "0");
        const hoursSinceLastReminder = Math.floor((currentTime - lastReminderTime) / (1000 * 60 * 60));
        if (hoursSinceLastReminder > 8) {
          localStorage.setItem(`lastEinvoiceReminderTimeMs.${selectedOrgDetails?.id}`, currentTime);
          const toDate = format(new Date(), "yyyy-MM-dd");
          const fromDate = format(subDays(new Date(toDate), 45), "yyyy-MM-dd");
          fetchPendingEinvoices(fromDate, toDate).then((data) => {
            const problematicRecords = data.filter(obj => obj.days > 10);
            if (problematicRecords.length) {
              addPopupAlertToQueue(() => {
                const modalRef = { current: null };
                modalRef.current = Modal.confirm({
                  onOk: () => modalRef.current.destroy(),
                  closable: false,
                  okText: "Okay",
                  icon: null,
                  cancelButtonProps: { style: { visibility: "hidden" } },
                  title: <div style={{ fontWeight: "bold", color: "#d9363e" }}>Pending E-Invoice Reminder</div>,
                  styles: {
                    body: { background: "#fff2f0" },
                    content: { background: "#fff2f0" },
                  },
                  content: <div>There are e-invoices pending for entries older than 10 days.
                    Please generate e-invoices on time to avoid compliance issues.
                  </div>
                });
              })
            }
          });
        }

      }
    }
  }, [selectedOrgDetails, screenLockPassed, companySelectorPopupVisible]);

  useEffect(() => {
    if (selectedOrgDetails?.id && screenLockPassed && !companySelectorPopupVisible && cashAccounts?.length && finalReportPermission) {
      const currentTime = new Date().getTime();
      const lastReminderTime = Number(localStorage.getItem(`lastNegativeCashReminderTimeMs.${selectedOrgDetails?.id}`) || "0");
      const hoursSinceLastReminder = Math.floor((currentTime - lastReminderTime) / (1000 * 60 * 60));
      if (hoursSinceLastReminder > 8) {
        localStorage.setItem(`lastNegativeCashReminderTimeMs.${selectedOrgDetails?.id}`, currentTime);
        fetchReport({
          options: {
            org_id: { options: [selectedOrgDetails?.id] },
            from_date: selectedFcYearDetails?.date1,
            to_date: selectedFcYearDetails?.date2,
            acm_id: { options: cashAccounts.map(obj => obj.id) }
          },
          settings: {
            type: "LEDGER_REGISTER",
            columns: [
              { id: "date" },
              { id: "bal_type" },
              { id: "bal_amt" },
            ]
          }
        }).then(data => {
          const negativeCashRecord = data?.data?.find?.(obj => obj.bal_type === "Cr" && obj.bal_amt && obj.date);
          if (negativeCashRecord) {
            addPopupAlertToQueue(() => {
              const modalRef = { current: null };
              modalRef.current = Modal.confirm({
                onOk: () => modalRef.current.destroy(),
                closable: false,
                okText: "Okay",
                icon: null,
                cancelButtonProps: { style: { visibility: "hidden" } },
                title: <div style={{ fontWeight: "bold", color: "#d9363e" }}>Negative Cash Alert</div>,
                styles: {
                  body: { background: "#fff2f0" },
                  content: { background: "#fff2f0" },
                },
                content: <div>
                  Negative cash on {formatDate(negativeCashRecord.date)}.
                  Please make necessary corrections to fix negative cash.
                </div>
              });
            })
          }
        });
      }
    }
  }, [selectedOrgDetails, screenLockPassed, companySelectorPopupVisible, cashAccounts?.length])

  useEffect(() => {
    if (selectedOrgDetails?.id && screenLockPassed && profile.role !== "super-user" && !companySelectorPopupVisible) {
      const date = format(new Date(), "yyyy-MM-dd");
      const currentYear = Number(format(new Date(), "yyyy"))
      const fcYear = org.fc_years.find(obj => obj.date2 >= date && obj.date1 <= date) || {
        date1: String(currentYear),
        date2: String(currentYear)
      }
      const fcYearAboutToEnd = fcYear && differenceInDays(new Date(fcYear.date2), new Date(date)) <= 2;
      const nextFcYear = org.fc_years.sort((a, b) => a.date1 < b.date1 ? -1 : 1).find(obj => obj.date1 > fcYear?.date2);
      const newFcYearRequired = fcYearAboutToEnd && !nextFcYear;
      if (newFcYearRequired) {
        const newFcYearFrom = Number(fcYear.date2.slice(0, 4))
        const newFcYearTo = newFcYearFrom + 1;
        const newFcYearName = `${newFcYearFrom}-${String(newFcYearTo).slice(2, 4)}`;
        const modalRef = { current: null };
        modalRef.current = Modal.confirm({
          onOk: async () => {
            try {
              incrementPendingRequests();
              await dispatch(createNewFinancialYear());
              await sleep(500);
              Modal.info({
                title: `Year created successfully!`,
                content: `To change selected year, press Alt+F1`,
                cancelButtonProps: { style: { display: "none" } },
                focusTriggerAfterClose: false,
                onOk: () => {
                  modalRef.current.destroy();
                }
              })
            } finally {
              decrementPendingRequests();
            }
          },
          icon: <ExclamationCircleOutlined />,
          closable: false,
          okText: "Create",
          title: `Create new financial year ${newFcYearName}`,
        });
      }

      const backupPermission = (userOrgRole === userOrgRoles.OWNER || userOrgPermissions.settings?.update)
      const daysSinceBackup = differenceInDays(new Date(), new Date(selectedOrgDetails.backup_at || selectedOrgDetails.created_at));
      if (daysSinceBackup >= 30 && backupPermission) {
        const modalRef = { current: null };
        modalRef.current = Modal.confirm({
          onOk: () => {
            modalRef.current.destroy();
            addAppTab("/settings/backup", "Backup")
          },
          icon: <ExclamationCircleOutlined />,
          closable: false,
          okText: "Go to Backup",
          title: "Backup Reminder",
          content: (
            <div>
              {selectedOrgDetails.backup_at ? (
                <p>Last Backup: {format(new Date(selectedOrgDetails.backup_at), "dd-MM-yyyy h:m a")}</p>
              ) : (
                <p>You have not downloaded any backup yet. Please download backup to ensure best data safety.</p>
              )}
            </div>
          )
        });
      }
    }
  }, [selectedOrgDetails?.id, screenLockPassed, companySelectorPopupVisible]);

  useEffect(() => {
    if (isUpdateAvailable) {
      const updateAcknowledged = getIsUpdateAcknowledged();
      if (!updateAcknowledged) {
        // Trigger update notification
        const key = "AppUpdateAvailable";
        const btn = (
          <Space>
            <Button type="primary" onClick={updateApp}>
              Update Now
            </Button>
          </Space>
        );
        notification.open({
          type: "info",
          placement: "bottomRight",
          duration: 0,
          message: "Update available! 🎁",
          btn,
          key,
          onClose: acknowledgeUpdate,
        });
      }
    }
  }, [isUpdateAvailable]);

  useEffect(() => {
    const handleEffect = async () => {
      if (selectedOrgId && !requireAccessApproval) {
        try {
          incrementPendingRequests();
          dispatch(reset("masters"));

          const promises = [
            dispatch(
              loadMasters([
                "HAGM",
                "AGM",
                "ACM",
                "ITG",
                "ITM",
                "ITS",
                "JOB",
                "PACKING",
                "UNIT",
                "HSN",
                "TAX",
                "BANK",
                "PGM",
                "SALESPERSON",
                "CONTACT",
                "FIRM",
                "REMARK",
                "SUBDESIGN",
                "RATE_CATEGORY",
                "BRAND",
                "LUT",
              ])
            ),
            dispatch(loadSettings()),
            dispatch(loadPrintSettings()),
            dispatch(loadOrganizationDetails()),
          ];

          await Promise.all(promises);
        } finally {
          decrementPendingRequests();
        }
      }
    }
    handleEffect();
  }, [selectedOrgId, requireAccessApproval]);

  useEffect(() => {
    dispatch(reset("transactions"));
    dispatch(reset("openingTransactions"));
    // Reload accounts on changing fc year with same org to refresh account balances
    if (selectedFcYearId) {
      setTimeout(async () => {
        const selectedOrgId = getSelectedOrgId(store.getState());
        if (previousSelectedOrgId && selectedOrgId === previousSelectedOrgId) {
          try {
            incrementPendingRequests();
            dispatch(reset("masters.ACM"));
            if (selectedFcYearId) {
              await dispatch(loadMasters(["ACM"]));
            }
          } finally {
            decrementPendingRequests();
          }
        }
      }, 10);
    }
  }, [selectedFcYearId]);

  useEffect(() => {
    AuthenticatedRoutes.preload();
  }, []);

  return (
    <ConfigProvider form={{ validateMessages: { required: "Required field" } }}
      theme={{
        token: {
          colorPrimary: "#0092e0",
          colorText: "rgba(0,0,0,0.87)",
          fontSize: 14,
          fontFamily: "'Roboto', sans-serif",
          colorBorder: "#aaa",
          borderRadius: 2,
          colorLink: "#0092e0",
        },
        components: {
          Form: {
            itemMarginBottom: 8,
          },
          Input: {
            colorBgBase: "transparent",
            paddingContentHorizontal: 8,
            paddingContentVertical: 0,
          },
          Select: {
            colorBgBase: "transparent",
          },
          Table: {
            headerBg: "#eee",
            rowSelectedBg: "#FFEB3B",
            rowHoverBg: "#EEE",
            paddingContentVertical: 8,
            borderColor: "#999"
          },
          Tag: {
            lineHeight: 32,
            fontSize: 14,
          }
        }
      }}>
      <div id="page-loader">
        <div className="loader"></div>
      </div>
      {!profileLoaded ? (
        <div style={{ height: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
          Loading profile...
        </div>
      ) : (
        <Suspense fallback={SuspenseFallback}>
          <AuthenticatedRoutes />
        </Suspense>
      )}
      {!screenLockPassed && (
        <div style={{ position: "fixed", top: 0, left: 0, bottom: 0, right: 0, zIndex: 1400 }}>
          <UnauthenticatedRoutes />
        </div>
      )}
      {requireAccessApproval && (
        <div style={{ position: "fixed", top: 0, left: 0, bottom: 0, right: 0, zIndex: 1400 }}>
          <RequestAccess />
        </div>
      )}
    </ConfigProvider>
  );
};

export default App;
